import {
    all, call, cancelled, put, select, takeLatest, throttle,
} from 'redux-saga/effects';
import { WorkspacesOverlayTargets, WorkspacesOverlayTypes, WorkspaceTypeKeys } from 'common/store/workspaces/types';
import { tl } from 'common/services/i18nService';
import { closeWorkspaceOverlay, setWorkspaceOverlay } from 'common/store/workspaces/actions';
import { setCsrfToken, setUserInfo } from 'common/store/session/actions';
import { SearchFilters } from 'common/store/common/search/types';
import commonSearchQueryBuilder from 'common/services/search/commonSearchQueryBuilder';
import commonApiService from 'common/services/api/commonApiService';
import { SessionState } from 'common/store/session/reducer';
import { waitFor, userIsEvaluator } from 'common/util/utils';
import { SessionTypeKeys } from 'common/store/session/types';

import { ContextTypeKeys, WorkflowPrimaryActions } from '../context/types';
import apiService from '../../services/api/apiService';
import * as assessmentActions from './actions';
import {
    GradingWorkflowDefinitionPhase, ExplanationObject, AssessmentState, ChildAssessment,
} from './reducer';
import { getGradingWorkflowInfo, loadAssessmentRunSession } from '../context/actions';
import { ContextState } from '../context/reducer';

import { workflowTabs } from '../../features/workflowOverlay/reducer';
import { onTabChange } from '../../features/workflowOverlay/actions';
import { RootState } from '../rootReducer';
import { Committee, getCommitteeById } from '../../services/committeeService';
import { onMarkSchemeFileUploaded, resetMarkSchemeFileUpload } from '../optionsMenu/actions';
import { getPartyCommitteeIdForContext } from '../../services/gradingPartyService';
import { CommitteeType } from './constants';
import { toKnownPlagiarismServiceProvider } from '../../services/PlagiarismReportService';
import { USERQUESTION_INDEX } from '../common/search/constants';
import { setValidationWarning } from '../warning/action';
import { CommonTypeKeys } from '../common/actions';
import { getAssessmentState, getSessionState } from '../context/sagas';

export const getContext = (state: RootState) => state.context;
export const getAssessment = (state: RootState): AssessmentState => state.assessment;

function* callFetchAssessmentInfo() {
    try {
        const contextState: ContextState = yield select(getContext);
        const data = yield call(apiService.fetchGradingInfo, contextState.assessmentRunId);
        const {
            assessmentRunTitle, assessmentRunGradingStatus, assessmentRunStartTime, assessmentRunEndTime,
            committees, gradeType, gradeTypeValues, collaborativeEvaluatorId,
            useNewGradingTool, deliveringPartyId, usePlagiarismService, isAdaptive,
            explanationOfGradesDeadline, explanationOfGradesDeadlineEnabled, explanationOfGradesDirectSendingEnabled,
            explanationAdministrationEnabled, questionSetContentItemId, questionSetRevisionId, hideExplanation, isGradingReady,
            isGradingReadyWithoutEndTime, marksMode, confirmationMode, confirmedTime, gradingLevel, useGradingThresholds, externalSystem,
            evaluators, numberOfEvaluatorContributors, candidateCount, hideJaggedStatus,
            gradingInstructionsContentItemId, gradingInstructionsTitle, gradingInstructionsExtension, totalNumberOfDeliveriesCovered,
            syncedWithExternalSystem, autoSyncValue, lastSyncDate, totalNumberOfCandidates, totalNumberOfEvaluators,
            maxExtraTime, latestDeliveryDeadline, gradingReadyIssues, subjectCode, evaluationType,
            subjectTitle, variant, versionCode, isAutomatedTestConstructionEnabled,
            weightThresholdDraftId, questionAssignmentDraftId, gradingQuestionAssignmentEnabled,
            gradingQuestionAssignmentAccessLevel, gradingQuestionAssignmentAdminLevel, gradeTransferTimestamp, hasGradesToTransfer,
            hasQuestionWeights, hasThresholds, quickCriteriaEnabled, hasMpLogo, annotationsShared, atLeastOneCandidateConfirmed,
            mpLogoUrl, marksReportScoreDisplayMode, collaborativeMarking, studentPreviewEnabled, compositeInfo, includeBlankDeliveries,
            questionWeightOverrideEnabled, amqpEnabled, numberOfNonStimulusQuestions, hidePlagiarismFromGraders, decimalBandsCriteriaEnabled,
            answerKeyCorrectionEnabled, questionAnswerKeyCorrectionDraftId, externalTest, videoBasedOralExam, hasPlagiarismQuestionType,
            enablePlannerConfirmOnBehalfOfGraders, isFinalGradePublishedToStudentDashboard, workflowPhases,
            isAutoDeliveryEnabled, autoDeliveryAllowMultipleSubmission, finalGrade, round, externalLtiDeliveryStatus,
            isMarksOnlyExam, candidateGradingComplete, rubricsEnabled, isAssessmentPath, hideQuestionTitles,
            isWebRTCOralExam, isUploadedFilesPdfConversion, isGradingBeforeEndTimeEnabled, gradingOnAssessmentPathParentEnabled,
            compositeGradeCalculationRule, maxNumberOfAttempts, isMultipleAttemptsParent, isMultipleAttemptsChild, attemptNumber,
            enableBulkReleaseOfExplanations, numberOfCandidatesWithExplanationNotSent, displayAdditionalScore,
            hidePlagiarismFromGradersWhileMarking, antiPlagiarismServiceProvider, isDecoupleGradingOnParentFromChildrenEnabled, isCEQTest,
            availableEventFlagTypes, hasPullingSections, hasManualSelectionSections, supportedUiLanguages, hasNextAttemptCreated,
            isMAProgressionLogicEnabled, nextAttemptStartTime, nextAttemptEndTime, hasMaximaQuestions, gradingWorkspaceDetailViewEnabled,
            gradingPlagiarismColorDisabled, isGradingWorkflowsEnabledForTest, gradingWorkflowRunUserId, autoMarkBlankManualResponsesEnabled,
            displayIOPercentageScore,
            isStudentReportEnabled, isOriginalityReportSharingEnabled, isRequestOriginalityReportEnabled, originalityReportSharingMethod,
            marksReportEnabled, usePlagiarismExternalServicePolling, isGraderViewMarkingStatusFilterEnabled,
            isAIAssistedMarkingEnabled,
        } = data;

        const explanationSettings: ExplanationObject = {
            explanationOfGradesDeadline,
            explanationOfGradesDeadlineEnabled,
            explanationOfGradesDirectSendingEnabled,
            explanationAdministrationEnabled,
            hideExplanation,
        };

        let maxScore = data.maxScore;
        if (isAssessmentPath && compositeInfo?.children?.length > 0) {
            maxScore = compositeInfo?.children?.map((child: ChildAssessment) => child.maxScore)
                .reduce((sum: number, childMaxScore: number) => sum + childMaxScore);
        }

        yield put(assessmentActions.assessmentInfoLoaded({
            assessmentRunTitle,
            assessmentRunStartTime,
            assessmentRunEndTime,
            assessmentRunGradingStatus,
            gradeType,
            gradeTypeValues,
            collaborativeEvaluatorId,
            questionSetContentItemId,
            questionSetRevisionId,
            useNewGradingTool,
            deliveringPartyId,
            committees,
            usePlagiarismService,
            isAdaptive,
            explanationSettings,
            isGradingReady,
            isGradingReadyWithoutEndTime,
            marksMode,
            confirmationMode,
            confirmedTime,
            gradingLevel,
            useGradingThresholds,
            evaluators,
            maxScore,
            numberOfEvaluatorContributors,
            externalSystem,
            candidateCount,
            gradingInstructionsContentItemId,
            gradingInstructionsTitle,
            gradingInstructionsExtension,
            totalNumberOfDeliveriesCovered,
            hideJaggedStatus,
            syncedWithExternalSystem,
            autoSyncValue,
            lastSyncDate,
            totalNumberOfCandidates,
            totalNumberOfEvaluators,
            maxExtraTime,
            latestDeliveryDeadline,
            gradingReadyIssues,
            weightThresholdDraftId,
            questionAssignmentDraftId,
            subjectCode,
            subjectTitle,
            variant,
            versionCode,
            evaluationType,
            isAutomatedTestConstructionEnabled,
            gradingQuestionAssignmentEnabled,
            gradingQuestionAssignmentAccessLevel,
            gradingQuestionAssignmentAdminLevel,
            gradeTransferTimestamp,
            hasGradesToTransfer,
            hasQuestionWeights,
            hasThresholds,
            hasMpLogo,
            quickCriteriaEnabled,
            annotationsShared,
            atLeastOneCandidateConfirmed,
            mpLogoUrl,
            marksReportScoreDisplayMode,
            collaborativeMarking,
            studentPreviewEnabled,
            compositeInfo,
            includeBlankDeliveries,
            questionWeightOverrideEnabled,
            amqpEnabled,
            numberOfNonStimulusQuestions,
            hidePlagiarismFromGraders,
            hidePlagiarismFromGradersWhileMarking,
            decimalBandsCriteriaEnabled,
            answerKeyCorrectionEnabled,
            questionAnswerKeyCorrectionDraftId,
            externalTest,
            videoBasedOralExam,
            hasPlagiarismQuestionType,
            enablePlannerConfirmOnBehalfOfGraders,
            isFinalGradePublishedToStudentDashboard,
            workflowPhases,
            explanationAdministrationEnabled,
            isAutoDeliveryEnabled,
            autoDeliveryAllowMultipleSubmission,
            finalGrade,
            round,
            isMarksOnlyExam,
            externalLtiDeliveryStatus,
            candidateGradingComplete,
            rubricsEnabled,
            isAssessmentPath,
            hideQuestionTitles,
            isWebRTCOralExam,
            isUploadedFilesPdfConversion,
            isGradingBeforeEndTimeEnabled,
            gradingOnAssessmentPathParentEnabled,
            compositeGradeCalculationRule,
            maxNumberOfAttempts,
            isMultipleAttemptsParent,
            isMultipleAttemptsChild,
            attemptNumber,
            enableBulkReleaseOfExplanations,
            numberOfCandidatesWithExplanationNotSent,
            displayAdditionalScore,
            isDecoupleGradingOnParentFromChildrenEnabled,
            antiPlagiarismServiceProvider: toKnownPlagiarismServiceProvider(antiPlagiarismServiceProvider),
            isCEQTest,
            availableEventFlagTypes,
            hasPullingSections,
            hasManualSelectionSections,
            supportedUiLanguages,
            hasNextAttemptCreated,
            isMAProgressionLogicEnabled,
            nextAttemptStartTime,
            nextAttemptEndTime,
            hasMaximaQuestions,
            gradingWorkspaceDetailViewEnabled,
            gradingPlagiarismColorDisabled,
            displayIOPercentageScore,
            isStudentReportEnabled,
            isOriginalityReportSharingEnabled,
            originalityReportSharingMethod,
            isRequestOriginalityReportEnabled,
            marksReportEnabled,
            usePlagiarismExternalServicePolling,
            candidateSubmissionAccessLevel: data.candidateSubmissionAccessLevel,
            skipQuestionSubmissionReviewEnabled: data.skipQuestionSubmissionReviewEnabled,
            isGraderViewMarkingStatusFilterEnabled,
            isAIAssistedMarkingEnabled,
            isGradingWorkflowsEnabledForTest,
            gradingWorkflowRunUserId,
            autoMarkBlankManualResponsesEnabled,
        }));

        if (videoBasedOralExam) {
            yield put(loadAssessmentRunSession());
        }

        if (gradingInstructionsContentItemId) {
            yield put(onMarkSchemeFileUploaded({
                title: gradingInstructionsTitle,
                contentItemId: gradingInstructionsContentItemId,
                extension: gradingInstructionsExtension,
            }));
        } else {
            yield put(resetMarkSchemeFileUpload());
        }

        const user = {
            ...data.user,
            hasEventLogAccess: data?.hasEventLogAccess,
        };
        yield put(setUserInfo(user));
        yield put(setCsrfToken(data.csrfToken));
        if (!videoBasedOralExam) {
            yield put(getGradingWorkflowInfo());
        }

        if (isGradingWorkflowsEnabledForTest) {
            yield call(callFetchGradingWorkflowDefinition, contextState.assessmentRunId);
        }
    } catch (error) {
        if (error && error.errorType === 'FORBIDDEN') {
            yield put(assessmentActions.isAuthenticationError(true));
        }
        yield put({ error, type: 'FETCH_ASSESSMENT_INFO_FAILED' });
    }
}

function* callFetchGradingWorkflowDefinition(assessmentRunId: number) {
    try {
        const response = yield call(apiService.fetchGradingWorkflow, assessmentRunId);
        if (response?.definition) {
            const definition: GradingWorkflowDefinitionPhase[] = JSON.parse(response.definition);
            yield put(assessmentActions.gradingWorkflowDefinitionLoaded(definition, response?.status, response?.phases));
        }
    } catch (error) {
        yield put({ error, type: 'FETCH_GRADING_WORKFLOW_DEFINITION_FAILED' });
    }
}

function* callUpdateDeadline(action: any) {
    const contextState: ContextState = yield select(getContext);
    const controller = new AbortController();
    const newDeadline = action.payload;
    try {
        if (newDeadline) {
            yield call(apiService.editAssessmentRun, contextState.assessmentRunId, newDeadline);
        }
    } catch (error) {
        yield put({ error, type: 'UPDATE_EXPLANATION_DEADLINE_FAILED' });
    } finally {
        if (yield cancelled()) {
            controller.abort();
        }
    }
}

function* callCloseTest() {
    const contextState: ContextState = yield select(getContext);
    const controller = new AbortController();
    try {
        yield call(apiService.closeTest, contextState.assessmentRunId, controller.signal);
    } catch (error) {
        yield put({ error, type: 'CLOSE_TEST_FAILED' });
    } finally {
        if (yield cancelled) {
            controller.abort();
        }
    }
}

function* callOpenWorkFlowOverlay(tabIndex: number, options?: any) {
    const contextState: ContextState = yield select(getContext);
    const assessmentState: AssessmentState = yield select(getAssessment);
    const { evaluators } = assessmentState;
    const { partyState: { gradingPartyDocument } } = contextState;
    const selectedCommitteeId = getPartyCommitteeIdForContext(gradingPartyDocument);
    const workspaceOverlayName = WorkspacesOverlayTypes.WORKFLOW_OVERLAY;
    let workspaceTitle = tl('grading.overlay.workflow.title');
    let workspaceSecondaryTitle = null;

    if (options && options.candidateLevelGrading) {
        workspaceTitle = `${tl('grading.breadcrumb.candidate')} ${options.partyName}`;
        if (options.roundsSize > 1) {
            workspaceSecondaryTitle = tl('grading.committees.appeal');
        }
    } else if (evaluators && evaluators.length > 1) {
        workspaceTitle += ` ${tl('grading.overlay.workflow.forGraders')}`;
    } else if (evaluators && evaluators.length === 1) {
        workspaceTitle += ` ${evaluators[0].name}`;
    } else if (selectedCommitteeId) {
        const committee: Committee = getCommitteeById(selectedCommitteeId);
        workspaceTitle += ` ${tl('grading.overlay.workflow.forCommittee')} ${committee?.name}`;
        workspaceSecondaryTitle = committee?.type === CommitteeType.grading
            ? tl('grading.committees.grading')
            : tl('grading.committees.appeal');
    }

    yield put(setWorkspaceOverlay(workspaceOverlayName, WorkspacesOverlayTargets.APP_OVERLAY_TARGET, {
        workspaceTitle, workspaceSecondaryTitle, options: { ...options, defaultTabIndex: tabIndex },
    }));
    const featureId = `feature_${workspaceOverlayName}_${contextState.workspaceId}_overlay`;
    yield put(onTabChange(featureId, tabIndex));
}

function* callDownloadGradingProtocol() {
    const contextState: ContextState = yield select(getContext);
    let gradingProtocolUrl = `/admin#grading/finalGrades/${contextState.assessmentRunId}`;
    if (contextState.selectedCommitteeId > 0) {
        gradingProtocolUrl = `${gradingProtocolUrl}/${contextState.selectedCommitteeId}`;
    }
    window.open(gradingProtocolUrl);
}

function* callOnPrimaryActionClick(action: any) {
    switch (action.payload.actionName) {
    case WorkflowPrimaryActions.END_TEST:
        yield call(callCloseTest);
        break;
    case WorkflowPrimaryActions.SHARE_OWN_GRADES:
        yield call(callOpenWorkFlowOverlay, workflowTabs.SHARE.index, action.payload.options);
        break;
    case WorkflowPrimaryActions.ALIGN_GRADES:
        yield call(callOpenWorkFlowOverlay, workflowTabs.ALIGN.index, action.payload.options);
        break;
    case WorkflowPrimaryActions.CONFIRM_FINAL_GRADES:
        yield call(callOpenWorkFlowOverlay, workflowTabs.CONFIRM.index, action.payload.options);
        break;
    case WorkflowPrimaryActions.DOWNLOAD_PROTOCOL:
        yield call(callDownloadGradingProtocol);
        break;
    }
}

function* handleFetchAssessment(action: any) {
    yield call(callFetchAssessmentInfo);
    if (action.type === assessmentActions.ContextTypeKeys.UPDATE_ASSESSMENT_GRADING_PARTY_INFO) {
        yield put(assessmentActions.gradingPartyIndexUpdate(action.payload));
    }
}

function* closeOverlay() {
    yield put(closeWorkspaceOverlay());
}

function* loadAssessmentQuestionsAttachment() {
    const contextState: ContextState = yield select(getContext);
    const data = yield call(apiService.getAttachedFilesInfo, contextState.assessmentRunId);

    yield put(assessmentActions.assessmentQuestionsAttachmentInfoLoaded(data));
}

function* setValidationIssueData() {
    const sessionState: SessionState = yield select(getSessionState);
    if (!userIsEvaluator(sessionState?.user?.privileges)) {
        return;
    }

    const getCollaborativeEvaluatorId = (state: RootState): number => state.assessment.collaborativeEvaluatorId;
    const getUserId = (state: RootState): number => state.session.user.id;
    // wait for the assessment state to load before proceeding
    yield call(waitFor, (state: RootState) => getCollaborativeEvaluatorId(state) != null);
    yield call(waitFor, (state: RootState) => getUserId(state) != null);
    const assessmentState: AssessmentState = yield select(getAssessmentState);
    const contextState: ContextState = yield select(getContext);
    const { assessmentRunId } = contextState;
    const questionFilters: SearchFilters = {
        assessmentRunId,
        hasMaximaValidationIssue: true,
        should: {
            $nested: {
                '$path': 'evaluatorScores',
                'evaluatorScores.evaluatorId': [assessmentState.collaborativeEvaluatorId, sessionState.user.id],
                'evaluatorScores.maximaValidationIssueReviewed': false,
            },
            /* eslint-disable @typescript-eslint/camelcase */
            must_not: {
                $nested: {
                    '$path': 'evaluatorScores',
                    'evaluatorScores.evaluatorId': [assessmentState.collaborativeEvaluatorId, sessionState.user.id],
                    'evaluatorScores.maximaValidationIssueReviewed': '$exists',
                },
            },
        },
    };
    let questionInfo = commonSearchQueryBuilder.createEmptyQuery();
    questionInfo = commonSearchQueryBuilder.appendFilters(questionInfo, questionFilters);
    questionInfo = commonSearchQueryBuilder.appendSource(questionInfo, 'context');
    questionInfo = commonSearchQueryBuilder.setHitsPerPage(questionInfo, 1);
    questionInfo = commonSearchQueryBuilder.replaceColumns(questionInfo, [
        {
            name: 'partyName',
            sortField: 'partyName.raw',
            order: 'asc',
        },
        {
            name: 'questionNumber',
            order: 'asc',
        },
    ]);
    const questionSearchJSON: string = encodeURIComponent(JSON.stringify(questionInfo));
    const dataQuestion = yield call(commonApiService.fetchListLiveData, USERQUESTION_INDEX, questionSearchJSON);
    if (dataQuestion.hits && dataQuestion.hits.length > 0) {
        const {
            questionId, partyId, hasMaximaValidationIssue,
        } = dataQuestion.hits[0];

        yield put(setValidationWarning({
            questionId,
            partyId,
            hasMaximaValidationIssue,
        }));
    } else {
        yield put(setValidationWarning({
            questionId: -1,
            partyId: -1,
            hasMaximaValidationIssue: false,
        }));
    }
}

function* watchAssessmentInfoLoading() {
    yield throttle(
        10000,
        [
            ContextTypeKeys.ASSESSMENT_RUN_ID_UPDATED,
            assessmentActions.ContextTypeKeys.UPDATE_GRADING_INFO,
            assessmentActions.ContextTypeKeys.UPDATE_ASSESSMENT_GRADING_PARTY_INFO,
        ],
        handleFetchAssessment,
    );
}

function* watchExplanationOfGradesSettings() {
    yield takeLatest(assessmentActions.ContextTypeKeys.UPDATE_EXPLANATION_DEADLINE, callUpdateDeadline);
}

function* watchPrimaryActionClick() {
    yield takeLatest(assessmentActions.ContextTypeKeys.ON_PRIMARY_ACTION_CLICK, callOnPrimaryActionClick);
}

function* watchWorkspaceChange() {
    yield takeLatest(WorkspaceTypeKeys.WILL_CHANGE_WORKSPACE_ID, closeOverlay);
}

function* watchLoadAssessmentQuestionsAttachmentInfo() {
    yield takeLatest(assessmentActions.ContextTypeKeys.LOAD_ASSESSMENT_QUESTIONS_ATTACHMENT_INFO, loadAssessmentQuestionsAttachment);
}

function* watchSetValidationIssueData() {
    yield takeLatest([
        ContextTypeKeys.PARTY_ID_UPDATED,
        CommonTypeKeys.SCORE_UPDATE_NOTIFICATION,
        SessionTypeKeys.SET_USER_INFO],
    setValidationIssueData);
}

export function* rootAssessmentSaga() {
    yield all([
        watchAssessmentInfoLoading(),
        watchExplanationOfGradesSettings(),
        watchPrimaryActionClick(),
        watchWorkspaceChange(),
        watchLoadAssessmentQuestionsAttachmentInfo(),
        watchSetValidationIssueData(),
    ]);
}
